import {
  Card,
  FormField,
  Country,
  FormControl,
  IconButton,
  openDialog,
  closeCurrentDialog,
} from 'platform/components';
import {Grid, GridItem, Hide, HStack, VStack, Stack} from 'platform/foundation';

import {useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {
  CustomerSelectSearch,
  useEditCustomer,
  CustomerMatchOrCreate,
  getCustomerName,
  getPhoneNumber,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {useDriverCustomerData} from '../hooks/useDriverCustomerData';
import {DriverInformationFormFields} from '../types/driverInformationSchema';

interface DriverInformationSectionProps extends TestIdProps {
  control: FormControl<DriverInformationFormFields>;
  formApi: UseFormReturn<DriverInformationFormFields>;
  countriesOptions: Country[];
  phoneInputKey: number;
  refreshPhoneInputKey: () => void;
  isReadOnly?: boolean;
}

export const DriverInformationSection = (props: DriverInformationSectionProps) => {
  const {serviceCaseId} = useWorkshopUrl();
  const {formApi, countriesOptions, refreshPhoneInputKey} = props;
  const formApiRef = useRef<UseFormReturn<DriverInformationFormFields> | null>(null);

  formApiRef.current = formApi;

  const {customerData, fallbackCustomerData} = useDriverCustomerData({
    serviceCaseId,
    formApi,
  });

  const handleCustomerSelect = (data: {
    id: string;
    name: string;
    email?: string;
    prefix?: string;
    phoneNumber?: string;
    customerId: string;
    identityCards?: Array<{cardNumber?: string}>;
  }) => {
    const formattedPhoneNumber = getPhoneNumber(data.prefix, data.phoneNumber, countriesOptions);

    formApi.setValue('driverFullName', data.name);
    formApi.setValue('driverEmail', data.email);
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverIdentityCard', data.identityCards?.[0]?.cardNumber);
    formApi.setValue('driverId', data.customerId);
    formApi.setValue('contactPerson', data.customerId);
  };

  const handleCustomerCreated = (customer: CustomerResponseBodyV2) => {
    const formApi = formApiRef.current;
    if (!formApi) {
      return;
    }

    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('contactPerson', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    refreshPhoneInputKey();
    closeCurrentDialog();
  };

  const handleCreateCustomer = () => {
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={handleCustomerCreated}
        data-testid={suffixTestId('createCustomer', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );
  };

  const editCustomer = useEditCustomer(customerData ?? fallbackCustomerData, (customer) => {
    const formApi = formApiRef.current;
    if (!formApi) {
      return;
    }

    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('contactPerson', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    refreshPhoneInputKey();
  });

  const handleEditCustomer = () => {
    editCustomer?.();
  };

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.person.labels.contactInformation')}
      hasSeparator
    >
      <VStack spacing={4}>
        <FormField name="driverId" type="hidden" control={props.control} />
        <Hide when={props.isReadOnly}>
          <CustomerSelectSearch
            onCustomerSelect={handleCustomerSelect}
            onAfterCustomerSelect={refreshPhoneInputKey}
          />
        </Hide>

        <Stack
          width="100%"
          direction={['column', 'column', 'column', 'row']}
          spacing={4}
          align="flex-end"
        >
          <Grid columns={[2, 2, 2, 4]} grow={1} spacing={4} align="flex-end">
            <GridItem>
              <FormField
                label={i18n.t('general.labels.nameAndSurname')}
                name="driverFullName"
                type="text"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('driver', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                key={props.phoneInputKey}
                label={i18n.t('entity.phoneNumber.labels.number')}
                name="driverPhone"
                type="phone"
                isDisabled
                control={props.control}
                countries={countriesOptions}
                data-testid={suffixTestId('driver', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                label={i18n.t('general.labels.emailAddress')}
                name="driverEmail"
                type="email"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('email', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                label={i18n.t('general.labels.identityCard')}
                name="driverIdentityCard"
                type="text"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('identityCard', props)}
              />
            </GridItem>
          </Grid>

          <HStack spacing={1}>
            <IconButton
              onClick={handleEditCustomer}
              priority="secondary"
              icon="image/edit"
              isDisabled={props.isReadOnly}
              data-testid={suffixTestId('editCustomer', props)}
            />
            <IconButton
              onClick={handleCreateCustomer}
              priority="secondary"
              icon="content/add_circle"
              isDisabled={props.isReadOnly}
              data-testid={suffixTestId('createCustomer', props)}
            />
          </HStack>
        </Stack>
      </VStack>
    </Card>
  );
};
